import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState } from 'react';
import { SimpleSearch } from '../../../../../reduxToolkit/Search/SearchApi';
import {
    DEFAULT_LEFT,
    LEFT_FIRST_COLUMN_WITH_CHECKBOX,
    SEARCH_MODEL
} from "../../../../../components/table/Model/TableModel";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import { status } from "../../../../../enums/statusEnum";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import { permissionObj } from "../../../../../Helpers/permission";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import TableFunction from "../../../../../components/table/TableFunction";
import routes from "../../../../../Routes/routes";
import { IconCanceledByVisitorCard, ConnectIcon, SettingIcon, UpdateIconsOptions } from "../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../components/CustomChip";
import { statusBorderColor, statusColor, statusFontColor } from "../../../../../enums/statusColorEnum";
import {EMPTY_VALUES, errorMsg} from "../../../../../Apis/functions";
import { useNavigate } from "react-router-dom";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";
import { handleConnect, handleReset, handleSetConfig } from '../Model/Models';

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * ControllerTableApp handles displaying and managing the controllers table.
 * Provides pagination, selection, deletion, and search functionalities.
 *
 * @param {string} option - Table option used for API calls and deletion.
 * @param {Function} setCriteria - Sets the current search/filter criteria.
 * @param {Function} setBodyForExport - Stores the body for exporting data.
 * @param {string} searchElement - Current search input value.
 * @param {Function} setSearchElement - Updates the search input value.
 * @param {boolean} setSimpleSearch - Enables/disables simple search mode.
 * @param {Array} selectedList - List of currently selected controller records.
 * @param {Function} setSelectedList - Updates the selected record list.
 *
 * @returns {Object} - Table handlers, pagination, selection, and CRUD functions.
 */
const ControllerTableApp = ({
                                option,
                                setBodyForExport,
                                searchElement,
                                setSearchElement,
                                setSimpleSearch,
                                selectedList,
                                setSelectedList
                            }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [isAllChecked, setIsAllChecked] = useState(false);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const contractorId = sessionStorage.getItem('contractorId');
    const supplierId = sessionStorage.getItem('providerId');
    const [results, setResults] = useState('');
    const tableId = "tbl_ct_table";
    const searchId = "tbl_ct_search_id";
    const [modified, setModified] = useState(false);
    const deletePermission = (DEV || permission?.includes(permissionObj?.WEB_HID_DELETE));
    const leftPosition = deletePermission ? LEFT_FIRST_COLUMN_WITH_CHECKBOX : DEFAULT_LEFT;

    /**
     * Builds the table header (thead) for the controllers table.
     *
     * @returns {Array<JSX.Element>} Array of <CustomTh> elements representing column headers.
     */
    const getThead = () => [
        <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('ip')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('n_formats')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('n_sios')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('n_readers')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('n_inputs')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('n_outputs')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('n_acrs')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('status')}</CustomTh>
    ];

    /**
     * Builds the table body (tbody) displaying controller data.
     *
     * @returns {Object} Object containing the JSX body and raw data.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return { body: [], data: [] };

        const body = results?.content?.map((item) => (
            <>
                <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes="first-column">{item?.name}</CustomTd>
                <CustomTd tableId={tableId}>{item?.ipAddress}</CustomTd>
                <CustomTd tableId={tableId}>{item?.formats || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.sios || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.readers || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.inputs || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.outputs || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.acrs || EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomChip
                        variant={'large'}
                        background={statusColor[item?.statusId]}
                        fontColor={statusFontColor[item?.statusId]}
                        borderColor={statusBorderColor[item?.statusId]}
                        legend={t(status[item?.statusId]) || EMPTY_VALUES.DEFAULT}
                    />
                </CustomTd>
            </>
        ));

        return { body, data: results?.content };
    };

    /**
     * Generates action buttons (update/edit) for each table row.
     *
     * @param {Object} item - Controller record.
     * @param {number} index - Row index.
     * @returns {JSX.Element} Dropdown menu with available actions.
     */
    const getActionsButtons = (item, index) => (
        <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
            {(DEV || permission?.includes(permissionObj?.WEB_HID_UPDATE)) &&
            <DropDownMenuItem
                index={index}
                handleClose={handleClose}
                name={t('update')}
                icon={<UpdateIconsOptions />}
                url={routes.controller.update + item.id}
                id="btn_cta_update"
            />}
             <DropDownMenuItem
                index={index}
                name={t('connect')}
                icon={<ConnectIcon className='icon-color-general-950' />}
                click={() => handleConnect(dispatch, item.id)}
                id="btn_cta_connect"
            />
             <DropDownMenuItem
                index={index}
                name={t('set_config')}
                icon={<SettingIcon className='icon-color-general-950' />}
                 click={() => handleSetConfig(dispatch, item.id)}
                id="btn_cta_set_config"
            />
             <DropDownMenuItem
                index={index}
                name={t('reset')}
                icon={<IconCanceledByVisitorCard className='icon-color-general-950' />}
                 click={() => handleReset(dispatch, item.id)}
                id="btn_cta_reset"
            />
        </DropDownMenuProfile>
    );

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, key: searchElement });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data } } }) => {
            setResults(data);
        });
    }

    /**
     * Table utility hook to handle selection, pagination, search, and rendering.
     */
    const {
        thead, tbody, handleCheckboxChange, handleDeleteAll,
        pagination, page, rowsPerPage, openMenu, handleMenuToggle, handleClose
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage: 'controller_per_page',
        setIsAllChecked,
        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Deletes selected controller(s) from the backend.
     *
     * @param {Array} deleteItem - List of controller IDs to delete.
     */
    const deleteSelectedController = (deleteItem) => {
        if (deleteItem.length > 0) {
            dispatch(DeleteItemsApi({ tableName: option, body: deleteItem })).then(({ payload: { data: { success, code } } }) => {
                if (success){
                    setModified(true);
                    setIsAllChecked(false);
                    setSelectedList([]);
                }
                else errorMsg(code)
            });
        }
    };

    return {
        t,
        DEV,
        permission,
        navigate,
        selectedList,
        setBodyForExport,
        page,
        option,
        rowsPerPage,
        searchElement,
        setSearchElement,
        contractorId,
        supplierId,
        setSimpleSearch,
        tableId,
        searchId,
        thead,
        tbody,
        setResults,
        isAllChecked,
        deletePermission,
        handleDeleteAll,
        handleCheckboxChange,
        getActionsButtons,
        deleteSelectedController,
        pagination
    };
};

export default ControllerTableApp;
